import { TwinParameters } from '@teinor/erp/types/company/config/parameter'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import SingleFormLayout from '../../../../../../../baseComponents/Layout/SingleFormLayout'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { valueOrDefaultValue } from '../../../../../../../utils/globals/valueOrDefaultValue'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { dictTimeZones } from '@teinor/erp/types/globals/timeZone'
import { GroupPayload } from '../../../../../../../utils/reducers/groups/groups'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import { ConnectedProps, connect } from 'react-redux'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import { getISOCurrencyMain } from '../../../../../../../utils/reducers/getters'
import { ConfigStripeCustomRender } from './StripeConfig'
import { Fragment } from 'react'
import { ConfigPrestashopCustomRender } from './PrestashopConfig'
import { ConfigWordpressCustomRender } from './WordpressConfig'
import { EmailCompanyConfig } from './emailCompanyConfig'
import { ConfigShift4CustomRender } from './Shift4Config'

type MainParametersProps = ComponentWithPermissions & ReduxMainParameters & {
    basicData: TwinParameters
    getData: () => Promise<void>
}

interface TwinParametersCustom extends TwinParameters {
    textForRef: string
}

const MainParameters: React.FC<MainParametersProps> = ({ basicData, getData, userPermissions, groups }) => {
    const { t } = useTwinTranslation()
    const onOff = {
        'on': { name: t('on', 'Activado') },
        'off': { name: t('disabledS', 'Desactivado') },
    }
    const dictCompanyCustomerTypes = {
        0: { name: t('all', 'Todos') },
        1: { name: t('B2C', 'B2C') },
        2: { name: t('B2B', 'B2B')}
    }
    const fields: FormRender<TwinParametersCustom> = [
        {
            cols: 3,
            title: t('general', 'General'),
            elements: [
                {
                    name: 'timeZone',
                    label: t('timeZone', 'Zona Horaria'),
                    component: 'CustomSelectWithSearchBar',
                    items: dictTimeZones,
                },
            ]
        },
        {
            cols: 3,
            title: t('dataLoseParameters', 'Parámetros de pérdida de datos'),
            elements: [
                {
                    name: 'showUnsaveDataModal',
                    label: t('showUnsaveDataModal', 'Mostrar "Tienes datos sin guardar"'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'timeInactivityToCloseSession',
                    label: t('timeInactivityToCloseSession', 'Tiempo de inactividad para cerrar sesión'),
                    component: 'InputHour',
                    value: String(basicData.timeInactivityToCloseSession || '0')
                },
                {
                    name: 'askForEmployeeEveryTicket',
                    label: t('askForEmployeeEveryTicket', 'Preguntar por el empleado cada ticket'),
                    component: 'CustomSelect',
                    items: onOff
                },
            ]
        },
        {
            cols: 3,
            title: t('workingCalendarParameters', 'Parámetros de control horario'),
            elements: [
                {
                    name: 'needHolidayApprove',
                    label: t('needHolidayApprove', 'Las vacaciones necesitan ser aprobadas'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'approveHolidayGroupId',
                    label: t('groupNotificationHolidaysAbsences', 'Grupo que recibe las not. de vacaciones/ausencias'),
                    component: 'CustomSelect',
                    items: groups || {}
                },
                {
                    name: 'needAbsenceApprove',
                    label: t('needAbsencesApprove', 'Las ausencias necesitan ser aprobadas'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'requireUbicationOnWorking',
                    label: t('requireUbicationOnWorking', 'Ubicación necesaria para fichar'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'requireQrOnPunchIn',
                    label: t('requireQrOnPunchIn', 'Qr para fichar'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'restrictTimeControlAccess',
                    label: t('restrictTimeControlAccess', 'Restricción de acceso por control horario'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'restrictTimeControlAccessMinutesMargin',
                    label: t('restrictTimeControlAccessMarginInHours', 'Margen de restricción de acceso en horas'),
                    component: 'InputHour',
                    value: String(basicData?.restrictTimeControlAccessMinutesMargin || 0)
                },
                {
                    name: 'timeControlPdfText',
                    label: t('timeControlPdfTextAdd', 'Texto para añadir al PDF de control horario'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    defaultValue: basicData?.timeControlPdfText || ''
                },
                {
                    name: 'punchInWithWorkingCenter',
                    label: t('punchInWithWorkingCenter', 'Fichar con centro de trabajo'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'deadlineWorkdayEdit',
                    label: t('deadlineWorkdayEditDays', 'Plazo máximo edición de jornada laboral (en días)'),
                    component: 'InputWithLabelMargin',
                },
                {
                    name: 'maxAnualOvertimeHours',
                    label: t('maxAnualOvertimeHours', 'Máximo horas extra anuales'),
                    component: 'InputHourUnlimited',
                    value: String(basicData?.maxAnualOvertimeHours || 0)
                },
                {
                    name: 'halfDayHolidayInTurns',
                    label: t('turnsWithHoliday', 'Los turnos tienen en cuenta los días de vacaciones'),
                    component: 'CustomSelect',
                    items: onOff,
                },
            ]
        },
        {
            cols: 3,
            title: t('expensesParameters', 'Parámetros de gastos'),
            elements: [
                {
                    name: 'kmPrice',
                    label: t('kmPrice', 'Precio del Km'),
                    component: 'InputPriceStateFull',
                    CurrencyId: getISOCurrencyMain(),
                    defaultValue: parseFloat(String(basicData.kmPrice)) || 0
                },
                {
                    name: 'approveEmployeeExpenseGroupId',
                    label: t('groupNotificationExpenses', 'Grupo que recibe las not. de gastos'),
                    component: 'CustomSelect',
                    items: groups || {}
                },
            ]
        },
        {
            cols: 3,
            title: t('taskParameters', 'Parámetros de tareas'),
            elements: [
                {
                    name: 'assignTaskYourWorkingCenterByDefault',
                    label: t('assignYourWorkingCenterByDefault', 'Asignar tu centro de trabajo por defecto'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'canUseOtherProjectBags',
                    label: t('workInOtherCustomersBagsOfHours', 'Trabajar en otras bolsas de horas del cliente'),
                    component: 'CustomSelect',
                    items: onOff
                },
            ]
        },
        {
            cols: 3,
            title: t('customersParameters', 'Parámetros de clientes'),
            elements: [
                {
                    name: 'companyTypeOfCustomers',
                    label: t('companyTypeOfCustomers', 'Tipos de cliente de la empresa'),
                    component: 'CustomSelect',
                    items: dictCompanyCustomerTypes
                },
                {
                    name: 'showAllCustomersTogether',
                    label: t('showAllCustomersTogether', 'Mostrar todos los clientes juntos'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'haveLeads',
                    label: t('haveLeads', 'Tienes clientes potenciales'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'automaticCreateCustomerTaskProject',
                    label: t('automaticCreateCustomerTaskProject', 'Crear automáticamente un proyecto en el cliente'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'codeCustomerToShow',
                    label: t('codeCustomerToShow', 'Código cliente para mostrar'),
                    component: 'CustomSelect',
                    items: {
                        0: { name: t('id', 'ID') },
                        1: { name: t('codeCustomer', 'Código de cliente') },
                    }
                },
                {
                    name: 'showOnlyClientManagerEmployees',
                    label: t('showOnlyClientManagerEmployees', 'Mostrar sólo gestores de clientes'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'useSecondTax',
                    component: 'CustomSelect',
                    label: t('useRechargeByDefault', 'Usar recargo por defecto'),
                    items: onOff
                }
            ]
        },
        {
            cols: 1,
            title: t('integrationParameters', 'Parámetros de integración'),
            elements: [
                {
                    name: 'textForRef',
                    component: 'Custom',
                    render: ({ extraProps }) => <ConfigStripeCustomRender parameters={extraProps.parameters} getData={extraProps.getData} />,
                    extraProps: {
                        parameters: basicData,
                        getData: getData
                    }
                },
                {
                    name: 'textForRef',
                    component: 'Custom',
                    render: ({ extraProps }) => <ConfigShift4CustomRender parameters={extraProps.parameters} getData={extraProps.getData} />,
                    extraProps: {
                        parameters: basicData,
                        getData: getData
                    }
                },
                {
                    name: 'textForRef',
                    component: 'Custom',
                    render: ({ extraProps }) => <ConfigPrestashopCustomRender parameters={extraProps.parameters} getData={extraProps.getData}/>,
                    extraProps: {
                        parameters: basicData,
                        getData: getData
                    }
                },
                {
                    name: 'textForRef',
                    component: 'Custom',
                    render: ({ extraProps }) => <ConfigWordpressCustomRender parameters={extraProps.parameters} getData={extraProps.getData}/>,
                    extraProps: {
                        parameters: basicData,
                        getData: getData
                    }
                },
            ]
        },
        {
            cols: 1,
            title: t('contactEmailParameters', 'Parámetros de email de contacto'),
            elements: [
                {
                    name: 'textForRef',
                    component: 'Custom',
                    render: ({ extraProps }) => <EmailCompanyConfig getData={extraProps.getData} parameters={extraProps?.parameters} {...{ userPermissions }} />,
                    extraProps: {
                        parameters: basicData,
                        getData: getData
                    }
                },
            ]
        },
    ]
    const parsedFields = valueOrDefaultValue(fields, userPermissions, basicData)
    return (
        <Fragment>
            <SingleFormLayout action='/api/app/config/parameter/updateParameters' onSubmit={getData} sections={parsedFields} userPermissions={userPermissions} />
        </Fragment>
        
    )
}

const mainParametersDispatch = {
    setGroups: (payload: GroupPayload) => ({ type: 'CHANGE_GROUP', payload }),
}

export type ReduxMainParameters = ConnectedProps<typeof mainParametersConnect>
const mapMainParametersConnector = (state: AllReduxPayloads) => ({ groups: state.groups })
const mainParametersConnect = connect(mapMainParametersConnector, mainParametersDispatch)

const mainParametersConnectLoading = withLoading(MainParameters, [{ fetchUrl: '/api/app/group/getAllGroups', propName: 'groups', setFunctionName: 'setGroups' }])

const MainParametersConnect = mainParametersConnect(mainParametersConnectLoading)


export default MainParametersConnect